import React, { forwardRef, type HTMLProps, useMemo, useRef } from 'react';

import { FloatingPortal as Portal } from '@floating-ui/react';

import { useMergeRefs } from '../../hooks/useMergeRefs';

import { useFloatingContext } from './Floating.context';
import { type FloatingProps } from './Floating.types';

type FloatingContentProps = HTMLProps<HTMLDivElement> &
    Pick<FloatingProps, 'getPopupContainer' | 'destroyOnHide'>;

export const FloatingPortal = forwardRef<HTMLDivElement, FloatingContentProps>(
    ({ style, getPopupContainer, destroyOnHide, ...props }: FloatingContentProps, propRef) => {
        const context = useFloatingContext();
        const ref = useMergeRefs([context.refs.setFloating, propRef]);
        const hasBeenOpened = useRef(false);

        const root = useMemo(() => {
            if (
                getPopupContainer &&
                typeof getPopupContainer === 'function' &&
                context.refs.reference.current
            ) {
                return getPopupContainer(context.refs.reference.current as HTMLElement);
            }
            return typeof window !== 'undefined' ? window.document.body : null;
        }, [getPopupContainer, context.refs.reference.current]);

        if (context.open) {
            hasBeenOpened.current = true;
        }

        // Unmount the floating element when closed and `destroyOnHide` is `true`
        if (!context.isMounted && destroyOnHide) return null;

        // This ensures we don't mount the floating element if it hasn't been opened yet
        if (!context.isMounted && !hasBeenOpened.current) return null;

        return (
            <Portal root={root}>
                <div
                    ref={ref}
                    style={{
                        ...context.floatingStyles,
                        ...style,
                        ...(!context.isMounted &&
                            !destroyOnHide && {
                                display: 'none',
                                pointerEvents: 'none'
                            })
                    }}
                    {...context.getFloatingProps(props)}
                />
            </Portal>
        );
    }
);

FloatingPortal.displayName = 'FloatingPortal';
